import { useEffect, useState } from 'react';

import { CaretDownIcon, HamburgerMenuIcon } from '@radix-ui/react-icons';
import { useEditorState, type Editor } from '@tiptap/react';

import { Button } from '../../../../primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../primitives/dropdown-menu';

import { ListNotionItem } from '../../../list/list-notion-item';

import { cn } from '../../../../utils/cn';
import { Values } from '../../../../utils/types';
import { type List } from './list.type';

// The `CommandInput` uses this value to filter
const COMMAND_ID = {
  orderedList: 'Lista numerada ol numbers',
  bulletList: 'Lista con viñetas ul bullets',
} as const;

const COMMANDS: List<typeof COMMAND_ID>[] = [
  {
    id: COMMAND_ID.orderedList,
    title: 'Lista numerada',
    description: 'Crea una lista numerada.',
    icon: (
      <div className="flex">
        <span className="w-[6px] text-xs">1</span>
        <HamburgerMenuIcon />
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⇧</span>
        <span className="inline-block w-3 text-right">7</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    id: COMMAND_ID.bulletList,
    title: 'Lista con viñetas',
    description: 'Crea una lista con viñetas.',
    icon: (
      <div className="flex">
        <span className="w-[6px] text-xs">•</span>
        <HamburgerMenuIcon />
      </div>
    ),
    shortcut: (
      <>
        <span>⌘⇧</span>
        <span className="inline-block w-3 text-right">8</span>
      </>
    ),
    command: (editor: Editor) => editor.chain().focus().toggleBulletList().run(),
  },
];

interface DropdownListProps {
  editor: Editor;
}

/**
 * TODO: maybe this component should be a select or combobox, analyze it after faqs refactor
 */
export function DropdownList({ editor }: DropdownListProps) {
  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isBulletList: editor.isActive('bulletList'),
      isOrderedList: editor.isActive('orderedList'),
    }),
  });

  const [open, setOpen] = useState(false); // patch to remove "Blocked aria-hidden on an element because its descendant retained focus."
  const [option, setOption] = useState<Values<typeof COMMAND_ID> | ''>(''); // only one of these commands can be executed at a time.

  useEffect(() => {
    if (editorState.isBulletList) {
      setOption(COMMAND_ID.bulletList);
      return;
    }
    if (editorState.isOrderedList) {
      setOption(COMMAND_ID.orderedList);
      return;
    }
    setOption('');
  }, [editorState]);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={() =>
        // patch to remove "Blocked aria-hidden on an element because its descendant retained focus."
        // the fake timeout ensures to close the dropdown menu after any DOM event execution. It is a radix bug.
        setTimeout(() => {
          setOpen(!open);
        }, 100)
      }
    >
      <DropdownMenuTrigger asChild disabled={!editor.isEditable}>
        <Button
          aria-label="Opciones de formato de lista"
          title="Opciones de formato de lista"
          className={cn('w-14 justify-between gap-0 rounded-full px-2', {
            'bg-accent/50': option !== '',
          })}
          role="checkbox"
          variant="ghost"
          onClick={() => setOpen(true)}
        >
          {option === '' ? (
            <div className="flex">
              <span className="w-[6px]" />
              <HamburgerMenuIcon className="size-4" />
            </div>
          ) : option === COMMAND_ID.bulletList ? (
            <div className="flex">
              <span className="w-[6px] text-xs">•</span>
              <HamburgerMenuIcon className="size-4" />
            </div>
          ) : (
            <div className="flex">
              <span className="w-[6px] text-xs">1</span>
              <HamburgerMenuIcon className="size-4" />
            </div>
          )}
          <CaretDownIcon className="size-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[16.5rem]">
        <DropdownMenuGroup>
          {COMMANDS.map(({ id, title, description, icon, shortcut, command }) => (
            <DropdownMenuItem
              className={cn('first:mb-1 [&:hover_*]:text-accent-foreground!', {
                'bg-accent/50 text-accent-foreground': option === id,
              })}
              data-active={option === id ? 'true' : undefined}
              key={id}
              onSelect={() => {
                command(editor);
                // patch to remove "Blocked aria-hidden on an element because its descendant retained focus."
                // the fake timeout ensures to close the dropdown menu after any DOM event execution. It is a radix bug.
                setTimeout(() => {
                  setOpen(false);
                }, 100);
              }}
            >
              <ListNotionItem
                title={title}
                description={description}
                icon={icon}
                shortcut={shortcut}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
